/**
 * Audio Stream Service
 *
 * Provides Web Audio API fan-out pattern to split a single microphone stream
 * into two separate MediaStreams - one for MediaRecorder (recording) and one
 * for Web Speech API (voice stop detection).
 *
 * This solves the iOS/Android limitation where only one API can consume
 * the microphone at a time.
 */

import { getMicrophoneStream, releaseStream } from './microphoneService';

let audioContext = null;
let sourceNode = null;
let recordingDestination = null;
let recognitionDestination = null;

/**
 * Get split audio streams for simultaneous recording and speech recognition.
 * Uses Web Audio API to fan out a single microphone stream into two separate streams.
 *
 * @param {string} [deviceId] - Optional specific device ID to use
 * @returns {Promise<{recordingStream: MediaStream, recognitionStream: MediaStream, cleanup: () => void}>}
 */
export const getSplitStreams = async (deviceId = null) => {
  console.log('getSplitStreams called with deviceId:', deviceId || '(default)');

  // Get base microphone stream (uses cached stream from microphoneService)
  const micStream = await getMicrophoneStream(deviceId);

  // Log microphone stream details
  const micTracks = micStream.getAudioTracks();
  console.log('Microphone stream for split:', {
    trackCount: micTracks.length,
    tracks: micTracks.map(t => ({
      label: t.label,
      readyState: t.readyState,
      muted: t.muted,
      enabled: t.enabled
    }))
  });

  // Create AudioContext (or resume if suspended)
  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  console.log('AudioContext created, state:', audioContext.state, 'sampleRate:', audioContext.sampleRate);

  if (audioContext.state === 'suspended') {
    await audioContext.resume();
    console.log('AudioContext resumed, new state:', audioContext.state);
  }

  // Create source node from microphone stream
  sourceNode = audioContext.createMediaStreamSource(micStream);

  // Create two separate destination nodes for fan-out
  recordingDestination = audioContext.createMediaStreamDestination();
  recognitionDestination = audioContext.createMediaStreamDestination();

  // Connect source to both destinations (fan-out pattern)
  sourceNode.connect(recordingDestination);
  sourceNode.connect(recognitionDestination);

  // Log recording stream details
  const recordingTracks = recordingDestination.stream.getAudioTracks();
  console.log('Recording stream created:', {
    trackCount: recordingTracks.length,
    tracks: recordingTracks.map(t => ({
      label: t.label,
      readyState: t.readyState,
      muted: t.muted,
      enabled: t.enabled
    }))
  });

  return {
    recordingStream: recordingDestination.stream,
    recognitionStream: recognitionDestination.stream,
    cleanup
  };
};

/**
 * Check if split streams are currently active
 * @returns {boolean}
 */
export const hasActiveSplitStreams = () => {
  return audioContext !== null && audioContext.state !== 'closed';
};

/**
 * Clean up all audio resources.
 * Call this when recording stops to release AudioContext and streams.
 */
export const cleanup = () => {
  console.log('[audioStreamService] cleanup called');

  // Stop destination stream tracks BEFORE disconnecting
  // This prevents MediaStream tracks from remaining "live" and consuming resources
  if (recordingDestination?.stream) {
    recordingDestination.stream.getTracks().forEach(track => {
      track.stop();
      console.log('[audioStreamService] Stopped recording destination track:', track.label);
    });
  }
  if (recognitionDestination?.stream) {
    recognitionDestination.stream.getTracks().forEach(track => {
      track.stop();
      console.log('[audioStreamService] Stopped recognition destination track:', track.label);
    });
  }

  // Disconnect nodes
  if (sourceNode) {
    try {
      sourceNode.disconnect();
    } catch (e) {
      // Ignore - may already be disconnected
    }
    sourceNode = null;
  }

  // Close AudioContext
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {
      // Ignore close errors
    });
  }
  audioContext = null;

  // Clear destination references
  recordingDestination = null;
  recognitionDestination = null;

  // Release the underlying microphone stream so it can be re-acquired fresh
  // This is critical for subsequent recordings to work properly
  releaseStream();

  console.log('[audioStreamService] cleanup complete, microphone released');
};

/**
 * Get the current AudioContext state for debugging
 * @returns {string|null} 'running', 'suspended', 'closed', or null if no context
 */
export const getContextState = () => {
  return audioContext?.state ?? null;
};

/**
 * Get the current AudioContext instance.
 * Used by Vosk voice trigger to tap into the same audio stream.
 * @returns {AudioContext|null}
 */
export const getAudioContext = () => audioContext;

/**
 * Get the current source node (MediaStreamSourceNode).
 * Used by Vosk voice trigger to connect its processor to the mic stream.
 * @returns {MediaStreamAudioSourceNode|null}
 */
export const getSourceNode = () => sourceNode;
